import { deepClone } from "fast-json-patch/lib/core";
import { ActionTree } from "vuex";
import ReportStoreTypes from "./define-types.store";
import {
    IReportSendEmailModel,
    ReportSendEmailModel,
    SendEmailLookupInfosDto,
    ReportPrintTemplateModel
} from "../../../models/report";
import { ISendMessageState } from "./states.store";
import {PropertyServices} from "../../../services";
import {PROPERTY_TAB_RELATION} from "../../../common/property.constant";
import {PromiseHelper} from "../../../utils/util";

const ACTUAL_TENANT_RELATION = 2;

const sendMessageActions = <ActionTree<ISendMessageState, any>>{[ReportStoreTypes.Get.CHANGE_INFO_SENDEMAIL]: ({ getters, commit, dispatch }) => {
    let payload = getters[ReportStoreTypes.Get.GET_SENDEMAIL];
    let model = deepClone(payload);
    model.IsMailling = true;
    return getters.service[ReportStoreTypes.Service.CHANGE_INFO_SENDEMAIL](model)
        .then((result: IReportSendEmailModel) => {
            // the send-message modal unregisters this module when it closes; a response arriving afterwards has no form to update
            if (!getters[ReportStoreTypes.Get.GET_SENDEMAIL]) return Promise.resolve(result);
            // DELETE KEYS HAD VALUE = 0
            var reg = new RegExp("^[0-9]+$");
            Object.keys(result).forEach(key => {
                if (
                    result[key].constructor !== Array &&
                    reg.test(result[key]) &&
                    result[key] == 0
                )
                    result[key] = null;
            });

            let reportModel = new ReportSendEmailModel(deepClone(result));
            // open from agenda, send to owner
            if (model.PropertyDisabled) {
                reportModel.PropertyDisabled = true;
                reportModel.IsRequestDeliveryReceipt = false;
                reportModel.IsRequestReadReceipt = false;
                reportModel.IsTrackEmails = false;
            }
            if (model.PropertyDisabled) {
                reportModel.PersonDisabled = true;
            }

            if (!payload.ChangeInfoAction || payload.ChangeInfoAction === "INIT") {
                // WA-3207 Add default document when set in config message (design Message)
                reportModel.LookupInfos.Documents.forEach(element => {
                    if (element.IsIncludeInMail) {
                        reportModel.DocumentIds.push({ Module: element.Module, DocumentId: element.Id, ...element });
                    }
                });
                commit(ReportStoreTypes.Set.STORE_SENDEMAIL, reportModel);
                if (reportModel.DemandInfos.length > 0) {
                    commit(ReportStoreTypes.Set.DEMAND_INFOS, reportModel.DemandInfos)
                }
                if (reportModel.PersonInfos.length > 0) {
                    commit(ReportStoreTypes.Set.PERSON_INFOS, reportModel.PersonInfos)
                }
                return Promise.resolve(reportModel);
            } else {
                reportModel.LookupInfos = deepClone(payload.LookupInfos) || new SendEmailLookupInfosDto();
                // merge lookup
                Object.keys(result.LookupInfos).forEach(key => {
                    reportModel.LookupInfos[key] = result.LookupInfos[key];
                });   

                reportModel.DocumentIds.filter(x => !x.IsIncludeInMail);
                // WA-3207 Add default document when set in config message (design Message)
                reportModel.LookupInfos.Documents.forEach(element => {
                    if (element.IsIncludeInMail) {
                        const existAttachedDefaultDoc = reportModel.DocumentIds.find(x => x.Module === element.Module && x.DocumentId === element.Id);
                        // Check if doc exist before add
                        if (!existAttachedDefaultDoc) reportModel.DocumentIds.push({ Module: element.Module, DocumentId: element.Id, ...element });
                    }
                });

                commit(ReportStoreTypes.Set.STORE_SENDEMAIL, reportModel);
                if (reportModel.ChangeInfoAction === 'DEMAND') {
                    commit(ReportStoreTypes.Set.DEMAND_INFOS, reportModel.DemandInfos);
                } else {
                    // WA-3096 Remove person that user already remove
                    const personInfos = deepClone(getters[ReportStoreTypes.Get.PERSON_INFOS]);
                    const tempModel = deepClone(reportModel.PersonInfos);
                    personInfos.forEach(element => {
                        const index = tempModel.findIndex(x => x.Id === element.Id);
                        if (index > -1) tempModel[index].Email = element.Email;
                    });
                    commit(ReportStoreTypes.Set.PERSON_INFOS, tempModel);
                }
                return Promise.resolve(reportModel);
            }
        })
        .catch(error => Promise.reject(error));
    },

    /** CALL SERVICE TO POST DATA */
    [ReportStoreTypes.Set.POST_DATA_SENDEMAIL]: ({ getters, commit }, action: string) => {
        let propertyService = new PropertyServices();

        const getPropertyRelationsPromises = (propertyIds) => propertyIds
            .map(id => propertyService.getPropertyById(id, [PROPERTY_TAB_RELATION]));

        const checkPropertyActualTenant = (property, personId) =>
            property.Relations.some(r => r.RelationTypeId === ACTUAL_TENANT_RELATION && r.PersonId === personId);

        if (action === 'preview') {
            let payload = deepClone(getters[ReportStoreTypes.Get.GET_SENDEMAIL]);
            let id = 0;

            const sendPreviewEmail = (requestPayload) => getters.service[ReportStoreTypes.Service.SEND_PREVIEW_EMAIL](requestPayload)
                .then((data: any) => {
                    data.id = id;
                    return Promise.resolve(data);
                })
                .catch(error => {
                    // console.log(error);
                    return Promise.reject(error);
                });
            
            if (payload.DemandInfos.length > 1) {
                const info = getters[ReportStoreTypes.Get.DEMAND_INFOS].find(i => i.Id === payload.DemandIds[0]);
                if (info) {
                    id = info.Id;
                    payload.DemandIds = [info.Id]
                    payload.DemandInfos = [info];
                }
            }
            if (payload.PersonInfos.length > 1) {
                const info = getters[ReportStoreTypes.Get.PERSON_INFOS].find(i => i.Id === payload.PersonIds[0]);
                if (info) {
                    id = info.Id;
                    payload.PersonIds = [info.Id]
                    payload.PersonInfos = [info];
                }
            }

            if (payload.PersonIds.length && (payload.IsOwner || payload.IsActualTenant)) {
                const personId = payload.PersonIds[0];
                let propertyIds = []
                let propertyInfos = []
                let documentIds = []

                if (payload.IsActualTenant) {
                    return PromiseHelper.allSettled(getPropertyRelationsPromises(payload.PropertyIds))
                        .then(responses => {
                            responses.forEach(({status, value}) => {
                                if (status === 'fulfilled') {
                                    if (checkPropertyActualTenant(value, personId)) {
                                        propertyIds.push(value.Id)
                                    }
                                }
                            })
                            propertyInfos = payload.PropertyInfos.filter(property => propertyIds.indexOf(property.Id) !== -1);
                            documentIds = payload.DocumentIds.filter(document => propertyIds.indexOf(document.PropertyId) !== -1)
                            payload.PropertyInfos = propertyInfos
                            payload.PropertyIds = propertyIds
                            payload.DocumentIds = documentIds

                            return sendPreviewEmail(payload)
                        })
                } else if (payload.IsOwner) {
                    // WA-4945 Show owner property when send email if user select all relations
                    // propertyInfos = payload.PropertyInfos.filter(property => property.OwnerId === personId);
                    // propertyIds = propertyInfos.map(property => property.Id)
                    // documentIds = payload.DocumentIds.filter(document => propertyInfos.indexOf(document.PropertyId) !== -1)
                    // payload.PropertyInfos = propertyInfos
                    // payload.PropertyIds = propertyIds
                    // payload.DocumentIds = documentIds

                    return sendPreviewEmail(payload);
                }
            }
            return sendPreviewEmail(payload);
        } else {
            let payload = deepClone(getters[ReportStoreTypes.Get.GET_SENDEMAIL]);

            if (payload.Module === 1 || payload.Module === 2 || payload.Module === 14) {
                let ToEmails = '';
                payload.PersonInfos.forEach(element => {
                    ToEmails += `${element.Email},`;
                });
                payload.ToEmails = ToEmails.substring(0, ToEmails.length - 1);
            } else {
                let ToEmails = '';
                getters[ReportStoreTypes.Get.DEMAND_INFOS].forEach(element => {
                    ToEmails += `${element.Email},`;
                });
                payload.ToEmails = ToEmails.substring(0, ToEmails.length - 1);
            }
            payload.IsMailling = true;

            if (payload.IsActualTenant) {
                return PromiseHelper.allSettled(getPropertyRelationsPromises(payload.PropertyIds))
                    .then(responses => {
                        const loadedProperties: any[] = responses.map((response) => response.status === 'fulfilled' && response.value);
                        const promises = payload.PersonInfos.map(personInfo => {
                            const personalPropertyIds = loadedProperties.filter(property => checkPropertyActualTenant(property, personInfo.Id))
                                .map(property => property.Id)
                            const personalPayload = {
                                ...payload,
                                PersonInfos: [personInfo],
                                // PropertyIds: personalPropertyIds,
                                PropertyInfos: payload.PropertyInfos.filter(property => personalPropertyIds.indexOf(property.Id) !== -1),
                                DocumentIds: payload.DocumentIds.filter(document => personalPropertyIds.indexOf(document.PropertyId) !== -1),
                                PersonIds: [personInfo.Id]
                            }
                            return getters.service[ReportStoreTypes.Service.SEND_SENDEMAIL](personalPayload)
                        });

                        return PromiseHelper.allSettled(promises)
                    })

            } else if (payload.IsOwner) {
                const promises = payload.PersonInfos.map(personInfo => {
                    // WA-4945 Show owner property when send email if user select all relations
                    // const perOwnerPropertyInfos = payload.PropertyInfos.filter(property => property.OwnerId === personInfo.Id);

                    // Allow send all attached document from persons and property
                    // const perOwnerPropertyIds: number[] = perOwnerPropertyInfos.map(propertyInfo => propertyInfo.Id)
                    // const perOwnerDocumentIds = payload.DocumentIds.filter(document => perOwnerPropertyIds.indexOf(document.PropertyId) !== -1)
                    const perOwnerPayload = {
                        ...payload,
                        PersonInfos: [personInfo],
                        // PropertyIds: perOwnerPropertyIds,
                        PersonIds: [personInfo.Id]
                    }

                    return Promise.resolve(getters.service[ReportStoreTypes.Service.SEND_SENDEMAIL](perOwnerPayload))
                });
                return PromiseHelper.allSettled(promises)
            } else {
                return getters.service[ReportStoreTypes.Service.SEND_SENDEMAIL](payload)
            }
        }
    },

    [ReportStoreTypes.Set.PREVIEW_ATTACHMENT]: ({ getters, commit }, payload: ReportPrintTemplateModel) => {
        return getters.service[ReportStoreTypes.Service.PREVIEW_ATTACHMENT](payload)
            .then((data: any) => {
                return Promise.resolve(data);
            })
            .catch(error => Promise.reject(error));
    },

    [ReportStoreTypes.Get.DESINGED_MESSAGE_TEMPLATES]: ({ getters, commit, dispatch }) => {
        const getSendEmailGetter = getters[ReportStoreTypes.Get.GET_SENDEMAIL];
        const properties = getSendEmailGetter.PropertyIds || [];
        const lang = getSendEmailGetter.LangId || 1;
        const messages = getSendEmailGetter.LookupInfos.Messages || [];
        const message = messages.find(e => e.Id === getSendEmailGetter.MessageId);

        let model = {
            objectID: properties.length > 0 ?  properties[0] : '',
            messageType: message? message.Type : '',
            language: lang
        };
        
        return getters.service[ReportStoreTypes.Service.DESINGED_MESSAGE_TEMPLATES](model)
            .then((result: any) => {
                let reportModel = result ? deepClone(result) : [];
                commit(ReportStoreTypes.Set.DESINGED_MESSAGE_TEMPLATES, reportModel);
                return Promise.resolve(reportModel);
            })
            .catch(error => Promise.reject(error));
        },
};

export default sendMessageActions;
